import type { LangCode, Plan, PlaybackMode } from "@1sky/shared";
import type { Session, SupabaseClient } from "@supabase/supabase-js";
import { useCallback, useEffect, useState } from "react";
import { accountsEnabled, config } from "../lib/config";

export interface Profile {
  id: string;
  display_name: string | null;
  home_language: LangCode;
  voice_consent: boolean;
  playback_mode: PlaybackMode;
  plan: Plan;
}

let client: Promise<SupabaseClient> | null = null;

/** Supabase is loaded on demand so the landing page doesn't pay for it. Null in guest-only mode. */
export function getSupabase(): Promise<SupabaseClient> | null {
  if (!accountsEnabled) return null;
  client ??= import("@supabase/supabase-js").then(({ createClient }) =>
    createClient(config.supabaseUrl, config.supabaseAnonKey, { auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true } }),
  );
  return client;
}

/** Every caller gets a session: an anonymous one is created on first use, so usage is tracked and can be upgraded later. */
export async function ensureSession(): Promise<Session | null> {
  const sb = await getSupabase();
  if (!sb) return null;
  const { data } = await sb.auth.getSession();
  if (data.session) return data.session;
  const { data: anon, error } = await sb.auth.signInAnonymously();
  if (error) {
    console.warn("anonymous sign-in failed", error.message);
    return null;
  }
  return anon.session;
}

export async function getAccessToken(): Promise<string | undefined> {
  return (await ensureSession())?.access_token;
}

const redirectTo = () => `${location.origin}/workspace`;

/** Magic link. Anonymous users keep their id (and history) by attaching the email to it. */
export async function signInWithEmail(email: string): Promise<void> {
  const sb = await getSupabase();
  if (!sb) throw new Error("accounts disabled");
  const { data } = await sb.auth.getUser();
  if (data.user?.is_anonymous) {
    const { error } = await sb.auth.updateUser({ email }, { emailRedirectTo: redirectTo() });
    if (!error) return;
    // Email already belongs to an account: sign in to that account instead.
  }
  const { error } = await sb.auth.signInWithOtp({ email, options: { emailRedirectTo: redirectTo(), shouldCreateUser: true } });
  if (error) throw error;
}

export async function signInWithGoogle(): Promise<void> {
  const sb = await getSupabase();
  if (!sb) throw new Error("accounts disabled");
  const { data } = await sb.auth.getUser();
  const opts = { provider: "google" as const, options: { redirectTo: redirectTo() } };
  const { error } = data.user?.is_anonymous ? await sb.auth.linkIdentity(opts) : await sb.auth.signInWithOAuth(opts);
  if (error) throw error;
}

export async function signOut(): Promise<void> {
  await (await getSupabase())?.auth.signOut();
}

export interface AuthState {
  loading: boolean;
  session: Session | null;
  profile: Profile | null;
  /** Signed in with email or Google (not just an anonymous guest). */
  signedIn: boolean;
  refresh: () => Promise<void>;
}

export function useAuth(): AuthState {
  const [state, setState] = useState<Omit<AuthState, "refresh">>({ loading: accountsEnabled, session: null, profile: null, signedIn: false });

  const refresh = useCallback(async () => {
    const sb = await getSupabase();
    if (!sb) return setState({ loading: false, session: null, profile: null, signedIn: false });
    const { data } = await sb.auth.getSession();
    const session = data.session;
    let profile: Profile | null = null;
    if (session) {
      const res = await sb.from("profiles").select("id, display_name, home_language, voice_consent, playback_mode, plan").eq("id", session.user.id).maybeSingle();
      profile = (res.data as Profile | null) ?? null;
    }
    setState({ loading: false, session, profile, signedIn: !!session && !session.user.is_anonymous });
  }, []);

  useEffect(() => {
    let unsub: (() => void) | undefined;
    void refresh();
    void getSupabase()?.then((sb) => {
      const { data } = sb.auth.onAuthStateChange(() => void refresh());
      unsub = () => data.subscription.unsubscribe();
    });
    return () => unsub?.();
  }, [refresh]);

  return { ...state, refresh };
}
